/**
 * Cart Upsell Slider
 * Displays recommended products in cart drawer with ability to add them
 */

function getCartDataService() {
  if (!window.__cartDataService) {
    window.__cartDataService = {
      currentPromise: null,
      lastData: null,
      fetch(force = false) {
        if (this.currentPromise && !force) {
          return this.currentPromise;
        }

        this.currentPromise = fetch('/cart.js', {
          cache: 'no-cache',
          headers: {
            'Accept': 'application/json'
          }
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Cart fetch failed with status ${response.status}`);
            }
            return response.json();
          })
          .then((data) => {
            this.lastData = data;
            return data;
          })
          .catch((error) => {
            console.error('Error fetching cart data:', error);
            throw error;
          })
          .finally(() => {
            this.currentPromise = null;
          });

        return this.currentPromise;
      },
      prime(data) {
        if (data) {
          this.lastData = data;
        }
      },
      getCached() {
        return this.lastData;
      }
    };
  }
  return window.__cartDataService;
}

class CartUpsell {
  constructor() {
    this.upsellContainer = null;
    this.upsellSlider = null;
    this.upsellSection = null;
    this.upsellProducts = [];
    this.cartItems = [];
    this.retryCount = 0;
    this.maxRetries = 10;
    this.observers = [];
    this.isInitializing = false;
    this.debounceTimer = null;
    this.lastInitializedSection = null;
    this.thirdBagAdded = false;
    this.currentDiscountCode = null;
    this.blendVariantIds = new Set();
    this.thirdBagLineKey = null;
    this.thirdBagVariantId = null;
    this.addingThirdBag = false;
    this.isProcessing = false; // Flag to prevent concurrent operations
    this.updateDebounceTimer = null; // Debounce timer for cart updates
    this.eventListenersSetup = false; // Track if listeners are already set up
    this.pendingRequests = new Set(); // Track pending add-to-cart requests by variant ID
    this.activeButtonClicks = new Set(); // Track active button clicks to prevent duplicates
    this.refreshingCartDrawer = false; // Flag to prevent concurrent cart drawer refreshes
    this.cartFetchPromise = null; // Shared promise for cart fetches
    this.discountRequestInFlight = null; // Prevent duplicate discount requests

    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.upsellSection = document.getElementById('cart-upsell-section');
    this.upsellSlider = document.getElementById('cart-upsell-slider');

    if (!this.upsellSection || !this.upsellSlider) {
      console.warn('Cart upsell elements not found, will retry when cart has items');
      this.setupRetryMechanism();
      return;
    }

    console.log('Cart upsell section found, loading products...');

    // Load upsell products and render
    this.loadUpsellProducts();

    // Listen for cart updates
    this.setupEventListeners();
  }
  
  setupWhenSectionAppears() {
    // Debounce to prevent multiple rapid calls
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      // Check if already initializing or if section is already initialized
      const upsellSection = document.getElementById('cart-upsell-section');
      if (!upsellSection || this.isInitializing) {
        return;
      }

      // Check if this is the same section we already initialized
      if (this.lastInitializedSection === upsellSection && this.upsellSection && this.upsellSection.isConnected) {
        return;
      }

      // This method is called when the section appears dynamically
      // Re-initialize elements and load products if not already loaded
      if (this.reinitializeElements()) {
        this.lastInitializedSection = upsellSection;
        this.isInitializing = true;
        
        // If products haven't been loaded, load them now
        if (!this.upsellProducts || this.upsellProducts.length === 0) {
          this.loadUpsellProducts().finally(() => {
            this.isInitializing = false;
          });
        } else {
          // Products are already loaded, just update the render
          this.updateUpsellProducts().finally(() => {
            this.isInitializing = false;
          });
        }
      }
    }, 200);
  }

  setupRetryMechanism() {
    console.log('Setting up retry mechanism for upsell section...');

    // Watch for cart drawer content changes - watch both #CartDrawer and .drawer__inner
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      // Watch #CartDrawer directly
      const cartDrawerElement = document.getElementById('CartDrawer');
      if (cartDrawerElement) {
        const mainObserver = new MutationObserver((mutations) => {
          // Only check if we don't have a connected section
          if (this.upsellSection && this.upsellSection.isConnected) {
            return;
          }
          
          mutations.forEach((mutation) => {
            if (mutation.type === 'childList') {
              // Check if upsell section now exists
              const upsellSection = document.getElementById('cart-upsell-section');
              if (upsellSection && (!this.upsellSection || !this.upsellSection.isConnected)) {
                this.setupWhenSectionAppears();
              }
            }
          });
        });
        mainObserver.observe(cartDrawerElement, { childList: true, subtree: true, attributes: true });
        this.observers.push(mainObserver);
      }

      // Also watch .drawer__inner
      const drawerInner = cartDrawer.querySelector('.drawer__inner');
      if (drawerInner) {
        const innerObserver = new MutationObserver((mutations) => {
          // Only check if we don't have a connected section
          if (this.upsellSection && this.upsellSection.isConnected) {
            return;
          }
          
          mutations.forEach((mutation) => {
            if (mutation.type === 'childList') {
              // Check if upsell section now exists
              const upsellSection = document.getElementById('cart-upsell-section');
              if (upsellSection && (!this.upsellSection || !this.upsellSection.isConnected)) {
                this.setupWhenSectionAppears();
              }
            }
          });
        });
        innerObserver.observe(drawerInner, { childList: true, subtree: true });
        this.observers.push(innerObserver);
      }
    }

    // Also listen for cart:updated events with multiple retries
    const checkForUpsellSection = () => {
      // Skip if already initialized and connected
      if (this.upsellSection && this.upsellSection.isConnected && this.upsellProducts.length > 0) {
        return;
      }
      
      const upsellSection = document.getElementById('cart-upsell-section');
      if (upsellSection) {
        this.retryCount = 0;
        this.setupWhenSectionAppears();
      } else if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        setTimeout(checkForUpsellSection, 150 * this.retryCount);
      } else {
        this.retryCount = 0;
      }
    };

    // Note: We don't add duplicate event listeners here - setupEventListeners() handles it
  }

  reinitializeElements() {
    this.upsellSection = document.getElementById('cart-upsell-section');
    this.upsellSlider = document.getElementById('cart-upsell-slider');

    if (!this.upsellSection || !this.upsellSlider) {
      console.warn('Upsell elements not found after re-initialization');
      return false;
    }

    console.log('Upsell elements re-initialized successfully');
    return true;
  }

  setupEventListeners() {
    // Prevent duplicate event listener setup
    if (this.eventListenersSetup) return;
    this.eventListenersSetup = true;

    // Debounced cart update handler to prevent multiple rapid calls
    const cartUpdateHandler = () => {
      // Clear any pending debounce timer
      if (this.updateDebounceTimer) {
        clearTimeout(this.updateDebounceTimer);
      }

      // Debounce the update to prevent cascading updates
      this.updateDebounceTimer = setTimeout(() => {
        if (this.isProcessing) {
          console.log('Cart update skipped - already processing');
          return;
        }

      if (this.upsellSection && this.upsellSection.isConnected) {
        // Just update, don't reinitialize
        this.updateUpsellProducts();
      } else {
        // Section doesn't exist yet, try to find it
        this.setupWhenSectionAppears();
      }
      }, 300); // 300ms debounce
    };

    // Only add one event listener for cart:updated
    document.addEventListener('cart:updated', cartUpdateHandler);

    // Listen for PUB_SUB_EVENTS if available (only if not already subscribed)
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined' && !this.pubSubUnsubscriber) {
      this.pubSubUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, cartUpdateHandler);
    }

    // Watch for cart drawer visibility and class changes - only once
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && !this.cartDrawerObserver) {
      const observer = new MutationObserver((mutations) => {
        // Skip if already initialized or processing
        if (this.upsellSection && this.upsellSection.isConnected || this.isProcessing) {
          return;
        }
        
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            if (!cartDrawer.classList.contains('is-empty')) {
              this.setupWhenSectionAppears();
            }
          }
        });
      });
      observer.observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
      this.cartDrawerObserver = observer;
      this.observers.push(observer);
    }
  }

  async loadUpsellProducts() {
    try {
      // Fetch cart to get current items
      const cartData = await this.fetchCartData({ force: true });

      // For now, we'll use a specific collection for upsell products
      // You can customize this by adding a metafield or setting in theme settings
      const collectionHandle = 'upsell-products'; // Change this to your collection handle

      const response = await fetch(`/collections/${collectionHandle}/products.json`, { cache: 'no-cache' });
      const data = await response.json();

      console.log('Upsell products loaded:', data.products); // Debug log

      if (data.products && data.products.length > 0) {
        this.upsellProducts = data.products.slice(0, 4); // Limit to 4 products
        this.populateBlendVariantIds();
        console.log('First product sample:', this.upsellProducts[0]); // Debug log
        await this.renderUpsellProducts({ cartData });
      } else {
        console.warn('No upsell products found in collection:', collectionHandle);
        this.hideUpsellSection();
      }
    } catch (error) {
      console.error('Error loading upsell products:', error);
      // If collection doesn't exist, try to get all products
      this.loadFallbackProducts();
    }
  }

  async loadFallbackProducts() {
    try {
      console.log('Loading fallback products from all products...');
      const response = await fetch('/products.json?limit=4', { cache: 'no-cache' });
      const data = await response.json();

      if (data.products && data.products.length > 0) {
        this.upsellProducts = data.products.slice(0, 4);
        this.populateBlendVariantIds();
        console.log('Fallback products loaded:', this.upsellProducts);
        await this.renderUpsellProducts();
      } else {
        this.hideUpsellSection();
      }
    } catch (error) {
      console.error('Error loading fallback products:', error);
      this.hideUpsellSection();
    }
  }

  async updateUpsellProducts() {
    // Prevent concurrent updates
    if (this.isProcessing) {
      console.log('Update skipped - already processing');
      return;
    }

    this.isProcessing = true;

    try {
      // First, ensure elements are initialized
      if (!this.upsellSection || !this.upsellSlider) {
        if (this.reinitializeElements()) {
          console.log('Upsell elements found during update');
        } else {
          console.warn('Upsell elements not found during update');
          this.isProcessing = false;
          return;
        }
      }

      // If products haven't been loaded yet, load them first
      if (!this.upsellProducts || this.upsellProducts.length === 0) {
        console.log('No upsell products loaded yet, loading them now...');
        await this.loadUpsellProducts();
        this.isProcessing = false;
        return;
      }

      // Fetch current cart items
      const cartData = await this.fetchCartData({ force: true });

      // Re-render with updated cart state (allowing auto-add for external updates)
      await this.renderUpsellProducts({ cartData });
    } catch (error) {
      console.error('Error updating upsell products:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  async renderUpsellProducts({ skipAutoAdd = false, cartData = null } = {}) {
    if (!this.upsellSlider) return;

    try {
      const currentCartData = cartData || await this.fetchCartData();
      this.cartItems = currentCartData.items || [];
      
      if (this.cartItems.length === 0) {
        this.thirdBagAdded = false;
        this.thirdBagLineKey = null;
        this.thirdBagVariantId = null;
        this.currentDiscountCode = null;
      }
      
      this.trackThirdBagLine();
      
      // Only ensure discounts if not skipping auto-add (to prevent cascading updates)
      if (!skipAutoAdd) {
        await this.ensureDiscounts();
      }
      
      const totalBlendQuantity = this.getBlendQuantity();
      const userBlendQuantity = this.getBlendQuantity(true);
      const hasSecondBagUnlocked = userBlendQuantity >= 2;

        let availableProducts = [...this.upsellProducts];

        // Filter out products already in cart
        availableProducts = availableProducts.filter(product => {
          const variantId = product.variants[0]?.id;
          return !this.cartItems.some(item => item.variant_id === variantId);
        });

        // Hide section if no products available
        if (availableProducts.length === 0) {
          this.hideUpsellSection();
          return;
        }

        // Sort products by price (cheapest first)
        availableProducts.sort((a, b) => {
          const priceA = a.variants[0].price || 0;
          const priceB = b.variants[0].price || 0;
          return priceA - priceB;
        });

        // Update header text to default
        const titleElement = this.upsellSection.querySelector('.cart-upsell-section__title');
        if (titleElement) {
          titleElement.textContent = "You might also like";
        }

        // Show section
        this.upsellSection.classList.remove('cart-upsell-section--empty');

        // Set data attribute for dynamic width
        this.upsellSlider.setAttribute('data-product-count', availableProducts.length);

        // Render products
        this.upsellSlider.innerHTML = availableProducts.map(product => {
          const variant = product.variants[0];
          const variantPriceValue = Number(variant.price) || 0;
          const price = this.formatMoney(variantPriceValue);

          // Get image - try featured_image first, then images array
          let imageSrc = '';
          if (product.featured_image) {
            imageSrc = product.featured_image;
          } else if (product.images && product.images.length > 0) {
            imageSrc = product.images[0].src || product.images[0];
          }

          const promoPriceBlock = `<p class="cart-upsell-product__price">${price}</p>`;

          return `
            <div class="cart-upsell-product" data-product-id="${product.id}" data-variant-id="${variant.id}">
              ${imageSrc ? `
                <img
                  src="${imageSrc}"
                  alt="${this.escapeHtml(product.title)}"
                  class="cart-upsell-product__image"
                  loading="lazy"
                >
              ` : '<div class="cart-upsell-product__image cart-upsell-product__image--placeholder"></div>'}
              <div class="cart-upsell-product__content">
              <div class="cart-upsell-product__info">
                <h4 class="cart-upsell-product__title">${this.escapeHtml(product.title)}</h4>
              </div>
              <div class="cart-upsell-product__price-cta-wrapper">
                  ${promoPriceBlock}
                <button
                  class="cart-upsell-product__add-button"
                  aria-label="Add ${this.escapeHtml(product.title)} to cart"
                  data-variant-id="${variant.id}"
                    data-is-blends="${hasBlendsTag}"
                >
                  Add
                </button>
                </div>
              </div>
            </div>
          `;
        }).join('');

        // Only auto-add third bag if not skipping (to prevent cascading updates)
        if (!skipAutoAdd) {
          await this.maybeAutoAddThirdBag();
        }

        // Add event listeners to add buttons (remove old ones first to prevent duplicates)
        this.upsellSlider.querySelectorAll('.cart-upsell-product__add-button').forEach(button => {
          // Remove any existing event listeners by cloning
          const newButton = button.cloneNode(true);
          button.parentNode.replaceChild(newButton, button);
          
          // Add single event listener with once option to prevent duplicates
          const clickHandler = (e) => {
            // Additional check: if button is already processing, ignore
            if (newButton.dataset.processing === 'true' || newButton.disabled) {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              return;
            }
            this.handleAddToCart(e);
          };
          
          newButton.addEventListener('click', clickHandler, { once: false, passive: false });
        });

        // Setup arrow navigation
        this.setupArrowNavigation();
      } catch (error) {
        console.error('Error rendering upsell products:', error);
      }
  }

  setupArrowNavigation() {
    if (!this.upsellSlider) return;

    const arrowLeft = document.getElementById('cart-upsell-arrow-left');
    const arrowRight = document.getElementById('cart-upsell-arrow-right');

    if (!arrowLeft || !arrowRight) return;

    // Update arrow states based on scroll position
    const updateArrowStates = () => {
      if (!this.upsellSlider) return;
      
      const { scrollLeft, scrollWidth, clientWidth } = this.upsellSlider;
      const isAtStart = scrollLeft <= 0;
      const isAtEnd = scrollLeft >= scrollWidth - clientWidth - 1;

      arrowLeft.classList.toggle('cart-upsell-section__arrow--disabled', isAtStart);
      arrowRight.classList.toggle('cart-upsell-section__arrow--disabled', isAtEnd);
    };

    // Check arrow states on scroll
    this.upsellSlider.addEventListener('scroll', updateArrowStates);
    
    // Check initial state
    updateArrowStates();

    // Left arrow - scroll left
    arrowLeft.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!this.upsellSlider) return;
      
      // Find the current visible product
      const products = this.upsellSlider.querySelectorAll('.cart-upsell-product');
      if (products.length === 0) return;
      
      const containerRect = this.upsellSlider.getBoundingClientRect();
      let currentIndex = -1;
      
      // Find which product is currently most visible
      products.forEach((product, index) => {
        const productRect = product.getBoundingClientRect();
        if (productRect.left >= containerRect.left && productRect.left < containerRect.right) {
          currentIndex = index;
        }
      });
      
      // If no product found, use the first one
      if (currentIndex === -1) {
        currentIndex = 0;
      }
      
      // Scroll to the previous product
      const prevIndex = Math.max(0, currentIndex - 1);
      products[prevIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    });

    // Right arrow - scroll right
    arrowRight.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!this.upsellSlider) return;
      
      // Find the current visible product
      const products = this.upsellSlider.querySelectorAll('.cart-upsell-product');
      if (products.length === 0) return;
      
      const containerRect = this.upsellSlider.getBoundingClientRect();
      let currentIndex = -1;
      
      // Find which product is currently most visible
      products.forEach((product, index) => {
        const productRect = product.getBoundingClientRect();
        if (productRect.left >= containerRect.left && productRect.left < containerRect.right) {
          currentIndex = index;
        }
      });
      
      // If no product found, use the first one
      if (currentIndex === -1) {
        currentIndex = 0;
      }
      
      // Scroll to the next product
      const nextIndex = Math.min(products.length - 1, currentIndex + 1);
      products[nextIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    });
  }

  async handleAddToCart(event) {
    // Prevent event bubbling and default behavior
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const button = event.currentTarget;
    const variantId = button.dataset.variantId;
    const blendVariantClicked = this.isBlendVariant(variantId);
    const productCard = button.closest('.cart-upsell-product');

    if (!variantId) {
      console.warn('No variant ID found');
      return;
    }

    const variantKey = String(variantId);
    const numericVariantId = Number(variantId);
    if (!numericVariantId) {
      console.warn('Invalid numeric variant ID', variantId);
      return;
    }

    // Create unique request ID for this click
    const requestId = `${variantKey}-${Date.now()}-${Math.random()}`;
    
    // Check if this exact button click is already being processed
    if (button.dataset.processing === 'true') {
      console.log('Button click already being processed');
      return;
    }

    // Check if this variant is already being added
    if (this.pendingRequests.has(variantKey)) {
      console.log(`Variant ${variantKey} already being added`);
      return;
    }

    // Check if we're already processing any add operation
    if (this.isProcessing) {
      console.log('Add to cart skipped - already processing');
      return;
    }

    // Mark button as processing
    button.dataset.processing = 'true';
    this.pendingRequests.add(variantKey);
    
    // Set processing flag to prevent concurrent adds
    this.isProcessing = true;

    // Disable button and show loading state
    button.disabled = true;
    button.textContent = 'Adding...';

    try {
      const formData = {
        items: [{
            id: numericVariantId,
          quantity: 1
        }]
      };

        console.log(`[${requestId}] Adding variant ${variantKey} to cart`);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
          body: JSON.stringify(formData),
          cache: 'no-cache'
      });

      if (!response.ok) {
        throw new Error('Failed to add product to cart');
      }

        console.log(`[${requestId}] Successfully added variant ${variantKey}`);

      // Mark as added
      productCard.classList.add('product-added');
      button.classList.add('added');
      button.textContent = 'Added';

        // Fetch updated cart (single source of truth)
        const cartData = await this.fetchCartData({ force: true });
        this.trackThirdBagLine();

      // Update cart drawer - this will refresh the cart items
      await this.refreshCartDrawer();

      // Re-initialize elements in case they were replaced
      this.reinitializeElements();
      
        // Now ensure discounts are applied
        await this.ensureDiscounts();

        // Re-render upsell products with updated cart items (skip auto-add to prevent loops)
        await this.renderUpsellProducts({ skipAutoAdd: true });

        // Trigger cart update event ONCE after all operations complete
        // This will notify other components but won't trigger our own update handlers
        // because we're using a debounce and processing flag
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { source: 'cart-upsell', cartData }
        }));

        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { 
            source: 'cart-upsell',
            cartData
          });
        }

    } catch (error) {
      console.error(`[${requestId}] Error adding product to cart:`, error);
      button.disabled = false;
      button.textContent = 'Add';
      button.dataset.processing = 'false';
      alert('Failed to add product. Please try again.');
    } finally {
      // Always clean up
      this.isProcessing = false;
      this.pendingRequests.delete(variantKey);
      button.dataset.processing = 'false';
    }
  }

  async ensureDiscounts() {
    // Promo logic disabled
    if (this.currentDiscountCode) {
      await this.removeDiscountCode();
    }
  }

  async applyDiscountCode(code) {
    if (!code || this.currentDiscountCode === code) return;
    if (this.discountRequestInFlight) {
      console.log('Discount request already in flight, skipping apply');
      return;
    }

    this.discountRequestInFlight = fetch(`/discount/${encodeURIComponent(code)}?redirect=/cart`, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-cache' // Prevent 304 responses
    })
      .then((response) => {
        if (response.ok || response.status === 200 || response.status === 302) {
          this.currentDiscountCode = code;
          console.log(`Discount code ${code} applied successfully`);
        } else {
          console.warn(`Discount code ${code} apply returned status ${response.status}`);
        }
      })
      .catch((error) => {
        console.error('Error applying discount code:', error);
      })
      .finally(() => {
        this.discountRequestInFlight = null;
      });

    return this.discountRequestInFlight;
  }

  async removeDiscountCode() {
    if (!this.currentDiscountCode) return;
    if (this.discountRequestInFlight) {
      console.log('Discount request already in flight, skipping removal');
      return;
    }

    this.discountRequestInFlight = fetch('/discount/clear', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      cache: 'no-cache'
    })
      .then(() => {
        this.currentDiscountCode = null;
        console.log('Discount code removed');
      })
      .catch((error) => {
        console.error('Error removing discount code:', error);
      })
      .finally(() => {
        this.discountRequestInFlight = null;
      });

    return this.discountRequestInFlight;
  }

  populateBlendVariantIds() {
    this.blendVariantIds = new Set();
    (this.upsellProducts || []).forEach((product) => {
      if (!product || !product.tags) return;
      const hasBlendTag = product.tags.some(tag => typeof tag === 'string' && tag.toLowerCase().includes('blends'));
      if (hasBlendTag && product.variants && product.variants[0]) {
        this.blendVariantIds.add(Number(product.variants[0].id));
      }
    });
  }

  getBlendQuantity(excludeAutoThirdBag = false) {
    if (!this.cartItems || this.blendVariantIds.size === 0) return 0;
    return this.cartItems.reduce((sum, item) => {
      if (item && this.blendVariantIds.has(Number(item.variant_id))) {
        if (excludeAutoThirdBag && item.properties && item.properties['_third_bag_auto']) {
          return sum;
        }
        return sum + (item.quantity || 0);
      }
      return sum;
    }, 0);
  }

  trackThirdBagLine() {
    this.thirdBagLineKey = null;
    this.thirdBagVariantId = null;
    this.thirdBagAdded = false;

    (this.cartItems || []).forEach((item) => {
      if (item && item.properties && item.properties['_third_bag_auto']) {
        this.thirdBagLineKey = item.key;
        this.thirdBagVariantId = item.variant_id;
        this.thirdBagAdded = true;
      }
    });
  }

  getDefaultBlendVariantId() {
    if (!this.blendVariantIds || this.blendVariantIds.size === 0) return null;
    const iterator = this.blendVariantIds.values().next();
    return iterator && iterator.value ? iterator.value : null;
  }

  async maybeAutoAddThirdBag() {
    // Third-bag promo disabled
    return;
  }

  async refreshCartDrawer() {
    // Prevent multiple simultaneous refreshes
    if (this.refreshingCartDrawer) {
      console.log('Cart drawer refresh already in progress, skipping...');
      return;
    }

    this.refreshingCartDrawer = true;

    try {
      const response = await fetch(`${window.location.pathname}?section_id=cart-drawer`, {
        cache: 'no-cache' // Prevent 304 responses
      });
      
      if (!response.ok) {
        console.warn('Cart drawer refresh failed:', response.status);
        return;
      }

      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');

      // Save the upsell section before replacing innerHTML
      const currentUpsellSection = document.getElementById('cart-upsell-section');
      const upsellSectionHTML = currentUpsellSection ? currentUpsellSection.outerHTML : null;

      // Update cart drawer items only
      const newCartDrawerItems = html.querySelector('cart-drawer-items');
      const currentCartDrawerItems = document.querySelector('cart-drawer-items');

      if (newCartDrawerItems && currentCartDrawerItems) {
        // Remove any existing upsell section from the new HTML before inserting
        const newUpsellSection = newCartDrawerItems.querySelector('#cart-upsell-section');
        if (newUpsellSection) {
          newUpsellSection.remove();
        }
        
        // Replace innerHTML
        currentCartDrawerItems.innerHTML = newCartDrawerItems.innerHTML;
        
        // Re-insert the saved upsell section if it existed
        if (upsellSectionHTML) {
          const cartItemsContent = currentCartDrawerItems.querySelector('#CartDrawer-CartItems');
          if (cartItemsContent) {
            // Parse the upsell section HTML and insert it
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = upsellSectionHTML;
            const upsellSectionElement = tempDiv.firstElementChild;
            
            // Find the cart items wrapper to insert before it
            const cartItemsWrapper = cartItemsContent.querySelector('.drawer__cart-items-wrapper');
            if (cartItemsWrapper) {
              cartItemsWrapper.parentNode.insertBefore(upsellSectionElement, cartItemsWrapper);
            } else {
              // If no wrapper, insert at the beginning of CartDrawer-CartItems
              cartItemsContent.insertBefore(upsellSectionElement, cartItemsContent.firstChild);
            }
          }
        }
      }

      // Update footer
      const newFooter = html.querySelector('.drawer__footer');
      const currentFooter = document.querySelector('.drawer__footer');

      if (newFooter && currentFooter) {
        currentFooter.innerHTML = newFooter.innerHTML;
      }

      // Update cart icon bubble
      const newBubble = html.querySelector('.cart-count-bubble');
      const currentBubble = document.querySelector('.cart-count-bubble');

      if (newBubble && currentBubble) {
        currentBubble.innerHTML = newBubble.innerHTML;
      }

    } catch (error) {
      console.error('Error refreshing cart drawer:', error);
    } finally {
      this.refreshingCartDrawer = false;
    }
  }

  hideUpsellSection() {
    if (this.upsellSection) {
      this.upsellSection.classList.add('cart-upsell-section--empty');
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  async fetchCartData({ force = false } = {}) {
    const cartService = getCartDataService();
    const data = await cartService.fetch(force);
    this.cartItems = data.items || [];
    return data;
  }

  isBlendVariant(variantId) {
    const numericVariantId = Number(variantId);
    if (!numericVariantId) return false;
    if (this.blendVariantIds && this.blendVariantIds.size) {
      return this.blendVariantIds.has(numericVariantId);
    }
    if (!Array.isArray(this.upsellProducts)) return false;
    return this.upsellProducts.some((product) => {
      if (!product || !product.variants || !product.variants[0]) return false;
      const variant = product.variants[0];
      if (Number(variant.id) !== numericVariantId) return false;
      return product.tags && product.tags.some(tag => tag.toLowerCase().includes('blends'));
    });
  }

  async addThirdBagVariant(sourceVariantId) {
    const fallbackVariantId = sourceVariantId || this.thirdBagVariantId || this.getDefaultBlendVariantId();
    if (!fallbackVariantId || this.addingThirdBag || this.isProcessing) {
      console.log('Third bag add skipped - already adding or processing');
      return;
    }

    const numericVariantId = Number(fallbackVariantId);
    if (!numericVariantId) {
      console.warn('Invalid variant ID for third bag', fallbackVariantId);
      return;
    }

    const variantKey = String(fallbackVariantId);

    // Check if this variant is already being added
    if (this.pendingRequests.has(variantKey)) {
      console.log(`Third bag variant ${variantKey} already being added`);
      return;
    }

    this.addingThirdBag = true;
    this.pendingRequests.add(variantKey);

    const requestId = `third-bag-${variantKey}-${Date.now()}`;
    console.log(`[${requestId}] Auto-adding third bag variant ${variantKey}`);

    try {
      const formData = {
        items: [{
          id: numericVariantId,
          quantity: 1,
          properties: {
            '_third_bag_auto': 'Auto-added third bag'
          }
        }]
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
        cache: 'no-cache' // Prevent caching
      });

      if (!response.ok) {
        throw new Error('Failed to auto-add third bag');
      }

      console.log(`[${requestId}] Successfully auto-added third bag`);

      // Fetch updated cart
      const updatedCart = await this.fetchCartData({ force: true });
      this.trackThirdBagLine();

      // Update cart drawer
      await this.refreshCartDrawer();
      this.reinitializeElements();
      
      // Re-render without triggering auto-add (skipAutoAdd = true)
      await this.renderUpsellProducts({ skipAutoAdd: true });

      // Ensure discounts are applied
      await this.ensureDiscounts();

      // Trigger cart update event ONCE after all operations complete
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { source: 'cart-upsell-third-bag', cartData: updatedCart }
      }));

      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { 
          source: 'cart-upsell-third-bag',
          cartData: updatedCart
        });
      }
    } catch (error) {
      console.error(`[${requestId}] Error auto-adding third bag:`, error);
    } finally {
      this.addingThirdBag = false;
      this.pendingRequests.delete(variantKey);
    }
  }

  async removeThirdBagLine() {
    if (!this.thirdBagLineKey) return;
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: this.thirdBagLineKey,
          quantity: 0
        })
      });

      this.thirdBagLineKey = null;
      this.thirdBagVariantId = null;
      this.thirdBagAdded = false;

      const updatedCart = await this.fetchCartData({ force: true });
      this.trackThirdBagLine();
      this.ensureDiscounts();
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { source: 'cart-upsell-third-bag-remove', cartData: updatedCart }
      }));
    } catch (error) {
      console.error('Error removing third bag:', error);
    }
  }

  formatMoney(price) {
    // Check if price is already in euro format (decimal) or cents (integer)
    let amount;
    if (typeof price === 'string') {
      amount = parseFloat(price);
    } else if (price > 1000) {
      // Likely in cents (e.g., 3995 = €39.95)
      amount = price / 100;
    } else {
      // Already in euros (e.g., 39.95)
      amount = price;
    }

    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  }
}

// Initialize when DOM is ready - ensure only ONE instance exists
let cartUpsellInstance = null;

function initializeCartUpsell() {
  // Prevent multiple instances
  if (cartUpsellInstance) {
    console.log('CartUpsell already initialized, skipping...');
    return;
  }

  cartUpsellInstance = new CartUpsell();
  window.cartUpsell = cartUpsellInstance;
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCartUpsell);
} else {
  // DOM already ready
  initializeCartUpsell();
}

